import { getFileExtension } from '@modules/product/util/classify-download';

/** Downloads with no category fall under this group heading. */
export const UNGROUPED_DOWNLOADS_TITLE = 'Downloads';

/** The minimal download shape the grouping/filetype helpers rely on. */
export interface DownloadFileFields {
    FileCategory?: { Title?: string | null; SortOrder?: number | null } | null;
    FileExtension?: string | null;
    ExternalLink?: string | null;
    InternalLink?: string | null;
}

/**
 * The download's category name, or an empty string when it has none. Space
 * around the name is not part of it, so `' Brochures '` and `'Brochures'` name
 * the same category; a title that is only whitespace names nothing at all, so
 * it counts as no category rather than as a category whose name happens to
 * render blank.
 */
export const downloadCategoryTitle = (download: DownloadFileFields): string =>
    download.FileCategory?.Title?.trim() ?? '';

export interface DownloadCategoryGroup<T> {
    title: string;
    sortOrder: number;
    items: T[];
}

/**
 * Groups downloads by their `FileCategory.Title` (the Spec Sheets / Brochures /
 * CAD sections), ordered by the category `SortOrder`. Downloads whose category
 * names nothing — absent, empty or whitespace only — fall under "Downloads",
 * which takes `Number.MAX_SAFE_INTEGER` as its position rather than any
 * `SortOrder` such a category carries, so it falls after every group that has
 * a real position. Group order follows first-seen for equal sort orders
 * (stable sort).
 */
export const groupDownloadsByCategory = <T extends DownloadFileFields>(
    downloads: readonly T[],
): DownloadCategoryGroup<T>[] => {
    const groups = new Map<string, DownloadCategoryGroup<T>>();

    for (const download of downloads) {
        const categoryTitle = downloadCategoryTitle(download);
        const title = categoryTitle || UNGROUPED_DOWNLOADS_TITLE;
        // Only a category that names something places its group. A category
        // whose title is blank is not the "Downloads" group's category — that
        // group holds the downloads that belong to no category at all — so it
        // does not lend it a position, and the group takes the same bottom
        // position as the downloads that have no category record.
        const sortOrder = categoryTitle
            ? (download.FileCategory?.SortOrder ?? Number.MAX_SAFE_INTEGER)
            : Number.MAX_SAFE_INTEGER;
        const existing = groups.get(title);
        if (existing) {
            existing.items.push(download);
        } else {
            groups.set(title, { title, sortOrder, items: [download] });
        }
    }

    return Array.from(groups.values()).sort(
        (a, b) => a.sortOrder - b.sortOrder,
    );
};

/**
 * The uppercase filetype label for a download — the backend `FileExtension`
 * when present, otherwise derived from the file link's extension (covers
 * external links that have no backend File). Empty string when unknown.
 */
export const downloadFileTypeLabel = (download: DownloadFileFields): string => {
    const extension =
        download.FileExtension ||
        getFileExtension(download.ExternalLink || download.InternalLink || '');
    return extension ? extension.toUpperCase() : '';
};
